<script setup>
import { onMounted, ref } from "vue";
import { Input as AInput, FormItem as AFormItem, Select as ASelect, SelectOption as ASelectOption } from "ant-design-vue";
import NoDataModal from "../no-data-modal/no-data-modal.vue";
import { debounce } from "../../helpers";
import axios from "axios";
import { useField } from "vee-validate";
import { useToast } from "vue-toastification";
import router from "@/router";
import store from "@/store";
import { mask } from "maska";

const PHONE_MASK = "+7 (XXX) XXX XX XX";

const visibleNoDataModal = ref(false);

const {value: iin, errorMessage: iinErrorMessage} = useField("iin");
const {value: name, errorMessage: nameErrorMessage} = useField("full_name");

const {value: victimMark, errorMessage: victimMarkErrorMessage} = useField("victim_vehicle_mark_model");
const {value: victimNumber, errorMessage: victimNumberErrorMessage} = useField("victim_vehicle_plate_number");

const {value: country, errorMessage: countryErrorMessage} = useField("country");
const {value: city, errorMessage: cityErrorMessage} = useField("city_id");
const {value: address, errorMessage: addressErrorMessage} = useField("address");
const {value: phone, errorMessage: phoneErrorMessage} = useField("phone");
const {value: email, errorMessage: emailErrorMessage} = useField("email");
const {value: applicantTypeISN, errorMessage: applicantTypeISNErrorMessage} = useField("applicant_type_isn");

// Потерпевший / Виновник / Представитель организации
const applicantTypes = ref([]);

// города
const citiesVariants = ref([]);

const toaster = useToast();

onMounted(() => {
    country.value = "Казахстан";

    axios.get("/V1/road-accidents/catalogs/official-cities").then(({data}) => {
        citiesVariants.value = data.map(({id, name}) => ({id: String(id), name}));
    }).catch(err => {
        if(err.status === 401){
            store.dispatch('auth/logoutTokenUnactive')
            router.push('/')
            return;
        }
        toaster.error(err.response.data.message)
    });

    axios.get(`/V1/road-accidents/catalogs/applicant-types`).then(({data}) => {
        applicantTypes.value = data.map(({id, name}) => ({id: String(id), name}));
    }).catch(err => {
        if(err.status === 401){
            store.dispatch('auth/logoutTokenUnactive')
            router.push('/')
            return;
        }
        toaster.error(err.response.data.message)
    });
})

const handleCheckUser = debounce(async (event) => {
        const value = event.target.value;

        if (value.length === 12) {
            email.value = "";
            phone.value = "";
            name.value = "";

            const {data} = await axios.get(`/V1/road-accidents/subject-info?iin=${value}`).catch(err => {
                toaster.error(err.response.data.message);
                visibleNoDataModal.value = true;
            });
            name.value = "";

            if (data.data.email) {
                email.value = data.data.email;
            }

            if (data.data.phone) {
                phone.value = mask(data.data.phone, PHONE_MASK);
            }

            if (data.data.surname) {
                name.value += data.data.surname + " ";
            }

            if (data.data.name) {
                name.value += data.data.name + " ";
            }

            if (data.data.patronymic) {
                name.value += data.data.patronymic;
            }   
        }
}, 1000);

const handleCloseNoDataModal = () => {
    visibleNoDataModal.value = false;
}
</script>

<template>
    <div class="applicant-details">
        <h2 class="applicant-details__title">Данные заявителя</h2>
        <div class="applicant-details__form">
            <AFormItem
                class="applicant-details__form-item"
                label="ИИН заявителя"
                :validate-status="iinErrorMessage"
                :help="iinErrorMessage ? iinErrorMessage : ''"
            >
                <AInput
                    :placeholder="'ИИН заявителя'"
                    class="questionnaire_card_input"
                    v-model:value="iin"
                    v-maska="'XXXXXXXXXXXX'"
                    @input="handleCheckUser"
                    :class="iinErrorMessage ? 'error' : ''"
                />
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="ФИО Заявителя"
                :validate-status="nameErrorMessage"
                :help="nameErrorMessage ? nameErrorMessage : ''"
            >
                <AInput
                    :placeholder="'Фамилия Имя Отчество'"
                    class="questionnaire_card_input"
                    v-model:value="name"
                    :class="nameErrorMessage ? 'error' : ''"
                />
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="Марка/модель ТС"
                :validate-status="victimMarkErrorMessage"
                :help="victimMarkErrorMessage ? victimMarkErrorMessage : ''"
            >
                <AInput
                    :placeholder="'Марка/модель ТС'"
                    class="questionnaire_card_input"
                    v-model:value="victimMark"
                    :class="victimMarkErrorMessage ? 'error' : ''"
                />
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="Гос.номер ТС"
                :validate-status="victimNumberErrorMessage"
                :help="victimNumberErrorMessage ? victimNumberErrorMessage : ''"
            >
                <AInput
                    :placeholder="'Гос.номер ТС'"
                    class="questionnaire_card_input"
                    v-model:value="victimNumber"
                    :class="victimNumberErrorMessage ? 'error' : ''"
                />
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="Страна"
                :validate-status="countryErrorMessage"
                :help="countryErrorMessage ? countryErrorMessage : ''"
            >
                <ASelect
                    class="questionnaire_card_input select"
                    placeholder="Страна"
                    v-model:value="country"
                    :class="countryErrorMessage ? 'error' : ''"
                    default-value="Казахстан"
                >
                    <ASelectOption value="Казахстан">
                        Казахстан
                    </ASelectOption>
                </ASelect>
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="Город"
                :validate-status="cityErrorMessage"
                :help="cityErrorMessage ? cityErrorMessage : ''"
            >
                <ASelect
                    class="questionnaire_card_input select"
                    placeholder="Город"
                    v-model:value="city"
                    :class="cityErrorMessage ? 'error' : ''"
                >
                    <ASelectOption
                        v-for="{id, name} of citiesVariants"
                        :value="id"
                        :key="id"
                    >
                        {{ name }}
                    </ASelectOption>
                </ASelect>
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="Адрес проживания"
                :validate-status="addressErrorMessage"
                :help="addressErrorMessage ? addressErrorMessage : ''"
            >
                <AInput
                    :placeholder="'Укажите адрес проживания или адрес прописки'"
                    class="questionnaire_card_input"
                    :class="addressErrorMessage ? 'error' : ''"
                    v-model:value="address"
                />
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="Номер телефона"
                :validate-status="phoneErrorMessage"
                :help="phoneErrorMessage ? phoneErrorMessage : ''"
            >
                <AInput
                    :placeholder="'+7 (000) 000 00 00'"
                    class="questionnaire_card_input"
                    v-maska="PHONE_MASK"
                    v-model:value="phone"
                    :class="phoneErrorMessage ? 'error' : ''"
                />
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="E-mail"
                :validate-status="emailErrorMessage"
                :help="emailErrorMessage ? emailErrorMessage : ''"
            >
                <AInput
                    :placeholder="'hello@mail.kz'"
                    class="questionnaire_card_input"
                    v-model:value="email"
                    :class="emailErrorMessage ? 'error' : ''"
                />
            </AFormItem>

            <AFormItem
                class="applicant-details__form-item"
                label="Тип заявителя"
                :validate-status="applicantTypeISNErrorMessage"
                :help="applicantTypeISNErrorMessage ? applicantTypeISNErrorMessage : ''"
            >
                <ASelect
                    :placeholder="'Тип заявителя'"
                    class="questionnaire_card_input select"
                    v-model:value="applicantTypeISN"
                    :class="applicantTypeISNErrorMessage ? 'error' : ''"
                >
                    <ASelectOption
                        v-for="{id, name} in applicantTypes"
                        :value="id"
                        :key="id"
                    >
                        {{ name }}
                    </ASelectOption>
                </ASelect>
            </AFormItem>
        </div>
    </div>

    <NoDataModal
        :visible="visibleNoDataModal"
        @close="handleCloseNoDataModal"
    />
</template>

<style>
.applicant-details {
    margin-top: 50px;
}

.applicant-details__title {
    font-family: "Roboto", sans-serif;
    font-size: 24px;
    color: #373737;
}

.applicant-details__form {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    column-gap: 20px;
}

.error {
    border: 1px solid red!important;
}

.select {
    .ant-select-selector {
        background-color: transparent !important;
        border: none !important;
        height: 55px !important;
    }

    .ant-select-selection-placeholder, .ant-select-selection-item {
        display: flex;
        align-items: center;
    }
}
</style>